import { type FormEvent, useEffect, useState } from "react";
import { ArrowRight, LockKeyhole, ShieldCheck } from "lucide-react";

import { api } from "../lib/api";

type PendingLink = { required: boolean; email: string; expires_in_seconds: number };
type LinkResult = { linked: boolean; next: string };

export function LinkAccountPage() {
  const [pending, setPending] = useState<PendingLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    api.get<PendingLink>("/api/v1/auth/oidc/link/", null)
      .then((result) => { if (active) setPending(result); })
      .catch(() => { if (active) setError("Cette demande de liaison a expiré. Recommencez avec SahelTech Compte."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!pending || busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await api.post<LinkResult>("/api/v1/auth/oidc/link/", { username, password }, null);
      if (!result.linked || !result.next || !result.next.startsWith("/") || result.next.startsWith("//")) {
        throw new Error("Réponse de liaison invalide.");
      }
      // Never persist the old password; the server now owns the customer session.
      window.location.assign(result.next);
    } catch (cause) {
      setPassword("");
      setError(cause instanceof Error ? cause.message : "Liaison impossible.");
    } finally {
      setBusy(false);
    }
  }

  return <main className="grid min-h-screen place-items-center bg-[#F6F8FB] px-5 py-10">
    <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-7 shadow-sm sm:p-10">
      <img src="/saheltech-mark.svg" alt="SahelTech" className="h-14 w-auto" />
      <div className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#EDF4FB] px-3 py-1.5 text-xs font-bold text-[#004A87]">
        <ShieldCheck className="h-4 w-4"/> Vérification de propriété
      </div>
      <h1 className="mt-5 text-3xl font-black text-[#0A192F]">Retrouver votre ancien espace</h1>
      <p className="mt-3 text-sm leading-6 text-slate-600">
        Votre compte SahelTech a été vérifié, mais un ancien compte Platform utilise déjà cette adresse.
        Confirmez vos anciens identifiants pour conserver vos entreprises et leurs données.
      </p>
      {loading && <p className="mt-6 text-sm text-slate-600">Vérification de la demande…</p>}
      {pending && <div className="mt-5 rounded-xl bg-[#F5F8FB] p-4 text-sm">
        Adresse SahelTech vérifiée : <strong>{pending.email}</strong>
        <p className="mt-2 text-xs text-slate-500">Cette confirmation expire sous cinq minutes. Aucun compte n'est associé avant votre validation.</p>
      </div>}
      {pending && <form className="mt-6 space-y-4" onSubmit={(e) => void submit(e)}>
        <label className="block text-sm font-semibold text-[#213C59]" htmlFor="legacy-username">Ancien identifiant Platform</label>
        <input id="legacy-username" className="field" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" maxLength={150} required />
        <label className="block text-sm font-semibold text-[#213C59]" htmlFor="legacy-password">Ancien mot de passe Platform</label>
        <input id="legacy-password" className="field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" maxLength={1024} required />
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? "Vérification…" : "Associer mes comptes"} <ArrowRight className="h-4 w-4"/>
        </button>
      </form>}
      {error && <p role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-800">{error}</p>}
      {!pending && !loading && <a className="btn-secondary mt-5" href="/api/v1/auth/oidc/start/">Recommencer la connexion</a>}
      <p className="mt-6 flex items-center gap-2 text-xs text-slate-500"><LockKeyhole className="h-4 w-4"/> Aucun mot de passe n'est envoyé à Accounts ou à Google.</p>
    </div>
  </main>;
}
